import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
  FormControlLabel,
  Checkbox,
  FormControl,
  Switch,
  FormHelperText,
  RadioGroup,
  Radio,
  FormLabel,
} from '@mui/material'
import {
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import { useState, useMemo } from 'react'
import { useCustom, useList } from '@refinedev/core'
import ErrorMessage from './ErrorMessage'

interface StatItem {
  category: string
  count: number
  region_ids?: number[]
  tag?: string
}

interface OpportunityTag {
  value: string
  name: string
  description: string
}

interface Region {
  id: number
  name: string
  ascii_name: string
  country: string
  region: string
  council: string
  major: string
  suburb: string
  additional_names: string[]
}

interface OppTagData {
  data: {
    chart_type:
      | 'tags'
      | 'regions'
      | 'base_regions'
      | 'tags_base_regions'
      | 'tags_regions'
      | 'count'
    stats: StatItem[]
    total_opportunities: number
  }
}

// Color palette for regions - more varied and distinguishable colors
const REGION_COLORS = [
  '#ef3636ff', // Red
  '#69fff5ff', // Teal
  '#45B7D1', // Sky Blue
  '#FFA07A', // Light Salmon
  '#98D8C8', // Mint
  '#F7DC6F', // Yellow
  '#BB8FCE', // Purple
  '#85C1E2', // Light Blue
  '#F8B739', // Orange
  '#52BE80', // Green
  '#EC7063', // Coral
  '#5DADE2', // Blue
  '#F5B7B1', // Pink
  '#FAD7A0', // Peach
  '#D7BDE2', // Lavender
  '#A9DFBF', // Light Green
  '#F9E79F', // Pale Yellow
  '#888c8fff', // Powder Blue
  '#E59866', // Tan
  '#A569BD', // Deep Purple
  '#73C6B6', // Turquoise
  '#F39C12', // Dark Orange
  '#3498DB', // Royal Blue
  '#E74C3C', // Bright Red
  '#9B59B6', // Violet
  '#1ABC9C', // Dark Teal
  '#E67E22', // Burnt Orange
  '#2ECC71', // Emerald
  '#F1C40F', // Bright Yellow
  '#34495E', // Dark Blue Gray
  '#16A085', // Dark Cyan
  '#D35400', // Pumpkin
  '#C0392B', // Dark Red
  '#8E44AD', // Deep Violet
  '#FF69B4', // Hot Pink
  '#20B2AA', // Light Sea Green
]

// Tag colors - same palette but will be mapped alphabetically
const TAG_COLORS = [...REGION_COLORS]

interface CategoryFilterProps {
  availableTags: string[]
  selectedTags: string[]
  tagMatchMode: 'any' | 'all' | 'exact'
  onTagsChange: (tags: string[]) => void
  onTagMatchModeChange: (mode: 'any' | 'all' | 'exact') => void
  disabled?: boolean
  helperText?: string
}

function CategoryFilter({
  availableTags,
  selectedTags,
  tagMatchMode,
  onTagsChange,
  onTagMatchModeChange,
  disabled = false,
  helperText = '',
}: CategoryFilterProps) {
  return (
    <>
      <Autocomplete
        multiple
        options={availableTags}
        value={selectedTags}
        onChange={(_, newValue) => {
          onTagsChange(newValue)
        }}
        disabled={disabled}
        getOptionLabel={(option) => option || ''}
        isOptionEqualToValue={(option, value) => option === value}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Filter by category"
            placeholder="Select categories..."
            helperText={helperText}
          />
        )}
        sx={{ mb: 1 }}
      />
      {selectedTags.length > 0 && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            backgroundColor: '#f5f5f5',
            borderRadius: 1,
          }}
        >
          <FormControl>
            <FormLabel sx={{ mb: 1, fontWeight: 600 }}>
              Tag Match Mode
            </FormLabel>
            <RadioGroup
              row
              value={tagMatchMode}
              onChange={(e) =>
                onTagMatchModeChange(e.target.value as 'any' | 'all' | 'exact')
              }
            >
              {selectedTags.length === 1 ? (
                <>
                  <FormControlLabel
                    value="any"
                    control={<Radio size="small" />}
                    label="Contains this tag"
                  />
                  <FormControlLabel
                    value="exact"
                    control={<Radio size="small" />}
                    label="Only this tag (and no others)"
                  />
                </>
              ) : (
                <>
                  <FormControlLabel
                    value="any"
                    control={<Radio size="small" />}
                    label="Either of these tags"
                  />
                  <FormControlLabel
                    value="all"
                    control={<Radio size="small" />}
                    label="All of these tags (may have others)"
                  />
                  <FormControlLabel
                    value="exact"
                    control={<Radio size="small" />}
                    label="Only these tags (and no others)"
                  />
                </>
              )}
            </RadioGroup>
          </FormControl>
        </Box>
      )}
    </>
  )
}

export default function OppTagStats() {
  // Filter state
  const [selectedRegion, setSelectedRegion] = useState<Region | null>(null)
  const [exactRegionMatch, setExactRegionMatch] = useState(false)
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [tagMatchMode, setTagMatchMode] = useState<'any' | 'all' | 'exact'>(
    'any'
  )
  const [baseRegionFilter, setBaseRegionFilter] = useState(true)

  // Data state
  const [stats, setStats] = useState<StatItem[]>([])
  const [chartType, setChartType] = useState<
    | 'tags'
    | 'regions'
    | 'base_regions'
    | 'tags_base_regions'
    | 'tags_regions'
    | 'count'
  >('tags')
  const [totalOpportunities, setTotalOpportunities] = useState<number>(0)

  // Fetch categories (tags)
  const { data: categories, isLoading: categoriesIsLoading } = useList({
    resource: 'opportunity_categories',
    config: {
      pagination: { current: 1, pageSize: 100 },
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting categories',
        description:
          error?.message || 'An error occurred while fetching categories',
        type: 'error',
      }
    },
  })

  const categoriesList = useMemo(
    () => (categories?.data as OpportunityTag[]) || [],
    [categories?.data]
  )

  const availableTags = useMemo(
    () =>
      categoriesList
        .map((cat) => cat.value)
        .filter((tag) => tag && tag.trim() !== '')
        .sort(), // Sort alphabetically for consistent color mapping
    [categoriesList]
  )

  // Create tag color map based on alphabetically sorted tags
  const tagColorMap = useMemo(() => {
    const map: Record<string, string> = {}
    availableTags.forEach((tag, index) => {
      map[tag] = TAG_COLORS[index % TAG_COLORS.length]
    })
    return map
  }, [availableTags])

  // Fetch regions
  const { data: regionOptions, isLoading: regionsIsLoading } = useList({
    resource: 'regions',
    meta: {
      paginate: false,
    },
    sorters: [
      {
        field: 'id',
        order: 'asc',
      },
    ],
  })

  const availableRegions = useMemo(
    () =>
      (regionOptions?.data as Region[])?.filter(
        (r) => r.name && r.name.trim() !== ''
      ) || [],
    [regionOptions?.data]
  )

  const availableBaseRegions = useMemo(
    () =>
      (regionOptions?.data as Region[])
        ?.filter((r) => r.name && r.name.trim() !== '')
        .filter((r) => !r.suburb || r.suburb.trim() == '')
        .sort((a, b) => {
          const countryA = a.country ? 1 : 0
          const countryB = b.country ? 1 : 0
          if (countryA !== countryB) {
            return countryA - countryB
          }

          const regionA = a.region ? 1 : 0
          const regionB = b.region ? 1 : 0
          if (regionA !== regionB) {
            return regionA - regionB
          }

          const councilA = a.council ? 1 : 0
          const councilB = b.council ? 1 : 0
          if (councilA !== councilB) {
            return councilA - councilB
          }

          const majorA = a.major ? 1 : 0
          const majorB = b.major ? 1 : 0
          return majorA - majorB
        }) || [],
    [regionOptions?.data]
  )

  // Build query params
  const queryParams = useMemo(() => {
    const params = new URLSearchParams()
    if (selectedRegion) {
      params.append('region', selectedRegion.id.toString())
    }
    if (exactRegionMatch) params.append('exact_region', 'true')
    selectedTags.forEach((tag) => params.append('tags', tag))
    if (selectedTags.length > 0) {
      params.append('tag_match_mode', tagMatchMode)
    }

    if (baseRegionFilter) params.append('base_region_filter', 'true')

    return params.toString()
  }, [
    selectedRegion,
    exactRegionMatch,
    selectedTags,
    tagMatchMode,
    baseRegionFilter,
  ])

  // API call for tag stats
  const { isLoading, isError } = useCustom({
    url: `/opportunities/tag-stats${queryParams ? `?${queryParams}` : ''}`,
    method: 'get',
    queryOptions: {
      onSuccess: (response: OppTagData) => {
        const data = response.data || response
        let statsData = data.stats || []

        // If chart type is tags and no tags are selected, fill in 0 counts for missing tags
        if (
          (data.chart_type === 'tags' ||
            data.chart_type === 'tags_base_regions' ||
            data.chart_type === 'tags_regions') &&
          selectedTags.length === 0
        ) {
          const statsMap = new Map(statsData.map((s) => [s.category, s]))
          // Start with existing stats
          const allStatsWithZeros = [...statsData]
          // Add any missing tags with 0 count
          availableTags.forEach((tag) => {
            if (!statsMap.has(tag)) {
              allStatsWithZeros.push({ category: tag, count: 0 })
            }
          })
          statsData = allStatsWithZeros
        }

        // Always sort by count descending
        statsData.sort((a, b) => b.count - a.count)

        setStats(statsData)
        setChartType(data.chart_type || 'tags')
        setTotalOpportunities(data.total_opportunities || 0)
      },
      onError: () => {},
    },
    meta: {
      queryKey: ['oppTagStats', queryParams],
    },
  })

  // Prepare pie chart data
  const pieChartData = useMemo(
    () =>
      stats
        .filter((stat) => stat.count > 0) // Exclude zero-count items from chart
        .map((stat) => {
          const percentage =
            totalOpportunities > 0
              ? ((stat.count / totalOpportunities) * 100).toFixed(1)
              : '0.0'
          return {
            name: `${stat.category} (${stat.count})`,
            value: stat.count,
            region_ids: stat.region_ids,
            tag: stat.tag,
            category: stat.category,
            percentage: percentage,
          }
        }),
    [stats, totalOpportunities]
  )

  // Handle click on pie chart segment
  const handlePieClick = (data: any) => {
    if (chartType === 'base_regions') {
      // Find the region by name and set as filter (use category not display name)
      const regionToAdd = availableBaseRegions.find(
        (r) => r.name === data.category
      )
      if (regionToAdd) {
        setSelectedRegion(regionToAdd)
        setBaseRegionFilter(true) // Ensure base region filter is on
      }
    } else if (chartType === 'regions') {
      // Find the specific region by name and set as filter
      const regionToAdd = availableRegions.find((r) => r.name === data.category)
      if (regionToAdd) {
        setSelectedRegion(regionToAdd)
        // Keep current base_region_filter setting
      }
    } else if (
      chartType === 'tags' ||
      chartType === 'tags_base_regions' ||
      chartType === 'tags_regions'
    ) {
      // Add tag to filter by name (use category not display name)
      const tagToAdd = data.category
      if (tagToAdd && !selectedTags.includes(tagToAdd)) {
        setSelectedTags([...selectedTags, tagToAdd])
      }
    }
  }

  // Dynamic labels based on chart type
  const chartTitle = useMemo(() => {
    let title = ''
    if (
      chartType === 'tags' ||
      chartType === 'tags_base_regions' ||
      chartType === 'tags_regions'
    ) {
      title = 'Category Breakdown'
    } else if (chartType === 'regions' || chartType === 'base_regions') {
      title = 'Location Breakdown'
    } else {
      return 'Total Count'
    }

    // Add filter context
    const filters: string[] = []
    if (selectedRegion) {
      filters.push(selectedRegion.name)
    }
    if (selectedTags.length > 0) {
      filters.push(selectedTags.join(', '))
    }

    if (filters.length > 0) {
      return `${title} for ${filters.join(' & ')}`
    }

    // If no filters and it's a category breakdown, add "Global"
    if (
      chartType === 'tags' ||
      chartType === 'tags_base_regions' ||
      chartType === 'tags_regions'
    ) {
      return `Global ${title}`
    }

    return title
  }, [chartType, selectedRegion, selectedTags])

  const tableHeader = useMemo(() => {
    if (
      chartType === 'tags' ||
      chartType === 'tags_base_regions' ||
      chartType === 'tags_regions'
    )
      return 'Category'
    if (chartType === 'regions' || chartType === 'base_regions') return 'Location'
    return 'Category'
  }, [chartType])

  return (
    <>
      {(isLoading || categoriesIsLoading || regionsIsLoading) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {isError && <ErrorMessage />}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-around',
          bgcolor: '#F2F2F2',
          p: 2,
          borderRadius: 1,
        }}
      >
        <Card elevation={2} sx={{ width: '100%', maxWidth: 1200 }}>
          <CardContent>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              Opportunity Tag Statistics
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={baseRegionFilter}
                  onChange={(e) => {
                    const newValue = e.target.checked
                    setBaseRegionFilter(newValue)
                    if (newValue) {
                      // When turning ON base region filter, disable exact region
                      setExactRegionMatch(false)
                    } else {
                      // When turning OFF base region filter, clear tags to avoid both being selected
                      if (selectedRegion && selectedTags.length > 0) {
                        setSelectedTags([])
                      }
                    }
                  }}
                  color="primary"
                  sx={{ mb: 5 }}
                />
              }
              label={
                <Box mb={2}>
                  Filter by base location
                  <FormHelperText>
                    Results will be filtered by locations that fall under the
                    location you select here - eg. Auckland, will show
                    consolidated results for all Auckland suburbs and locations
                  </FormHelperText>
                </Box>
              }
            />

            {/* Filters Section */}
            <Box sx={{ mb: 4 }}>
              {/* Base Region Filter */}
              {baseRegionFilter ? (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Autocomplete
                      options={availableBaseRegions}
                      value={selectedRegion}
                      onChange={(_, newValue) => {
                        setSelectedRegion(newValue)
                      }}
                      getOptionLabel={(option) => option.name || ''}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.name}

                          <span style={{ color: 'gray', marginLeft: '8px' }}>
                            - {option.country ? `${option.country}` : ''}
                            {option.region ? `, ${option.region}` : ''}
                            {option.council ? `, ${option.council}` : ''}
                            {option.major ? `, ${option.major}` : ''}
                          </span>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Filter by base location"
                          placeholder="Select a location..."
                        />
                      )}
                      sx={{ mb: 1 }}
                    />
                  </Box>
                  {/* Category Filter */}
                  <Box sx={{ mb: 2 }}>
                    <CategoryFilter
                      availableTags={availableTags}
                      selectedTags={selectedTags}
                      tagMatchMode={tagMatchMode}
                      onTagsChange={setSelectedTags}
                      onTagMatchModeChange={setTagMatchMode}
                    />
                  </Box>
                </>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'stretch', sm: 'flex-start' },
                    gap: 2,
                    mb: 2,
                    mx: 5,
                  }}
                >
                  {/* Specific Region Filter */}
                  <Box sx={{ flex: 1 }}>
                    <Autocomplete
                      options={availableRegions}
                      value={selectedRegion}
                      onChange={(_, newValue) => {
                        setSelectedRegion(newValue)
                        // Clear tags when selecting a region (mutually exclusive when base_region_filter is off)
                        if (newValue && !baseRegionFilter) {
                          setSelectedTags([])
                        }
                      }}
                      disabled={selectedTags.length > 0}
                      getOptionLabel={(option) => option.name || ''}
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderOption={(props, option) => (
                        <li {...props} key={option.id}>
                          {option.name}
                          {option.ascii_name &&
                            option.ascii_name !== option.name && (
                              <span
                                style={{ color: 'gray', marginLeft: '8px' }}
                              >
                                - {option.ascii_name}
                              </span>
                            )}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Filter by location"
                          placeholder="Select a location..."
                          helperText={
                            selectedTags.length > 0
                              ? 'Clear tags to filter by location'
                              : ''
                          }
                        />
                      )}
                    />
                    {!baseRegionFilter && (
                      <Box sx={{ mt: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={exactRegionMatch}
                              onChange={(e) =>
                                setExactRegionMatch(e.target.checked)
                              }
                            />
                          }
                          label="Only opportunities with exactly this location"
                        />
                      </Box>
                    )}
                  </Box>

                  {/* OR Divider */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      minHeight: '56px',
                    }}
                  >
                    <Typography
                      sx={{
                        px: { xs: 0, sm: 1 },
                        py: { xs: 1, sm: 0 },
                        fontWeight: 'bold',
                        color: '#666',
                      }}
                    >
                      OR
                    </Typography>
                  </Box>

                  {/* Category Filter */}
                  <Box sx={{ flex: 1 }}>
                    <CategoryFilter
                      availableTags={availableTags}
                      selectedTags={selectedTags}
                      tagMatchMode={tagMatchMode}
                      onTagsChange={(newValue) => {
                        setSelectedTags(newValue)
                        // Clear region when selecting tags (mutually exclusive when base_region_filter is off)
                        if (newValue.length > 0 && !baseRegionFilter) {
                          setSelectedRegion(null)
                        }
                      }}
                      onTagMatchModeChange={setTagMatchMode}
                      disabled={!baseRegionFilter && selectedRegion !== null}
                      helperText={
                        !baseRegionFilter && selectedRegion
                          ? 'Clear location to filter by category'
                          : ''
                      }
                    />
                  </Box>
                </Box>
              )}
            </Box>

            {/* Chart Title */}
            <Typography variant="h6" gutterBottom sx={{ mt: 6 }}>
              {chartTitle}
            </Typography>

            {/* Total Count */}
            <Typography variant="body2" gutterBottom>
              Total Opportunities: <strong>{totalOpportunities}</strong>
            </Typography>
            {(chartType === 'regions' || chartType === 'base_regions') && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3, fontStyle: 'italic' }}
              >
                Note: Opportunities can have multiple locations, so counts may
                exceed the total opportunities.
              </Typography>
            )}
            {(chartType === 'tags' ||
              chartType === 'tags_base_regions' ||
              chartType === 'tags_regions') &&
              totalOpportunities > 0 && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 3, fontStyle: 'italic' }}
                >
                  Note: Opportunities can have multiple tags, so counts may
                  exceed the total.
                </Typography>
              )}

            {/* Chart Visualization */}
            {chartType !== 'count' &&
              pieChartData.length > 0 &&
              pieChartData.length <= availableTags.length && (
                <Box
                  sx={{
                    height: 500,
                    mb: 3,
                    overflowY: 'auto',
                    position: 'relative',
                  }}
                >
                  <ResponsiveContainer width="100%" height={500}>
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        outerRadius={150}
                        fill="#8884d8"
                        dataKey="value"
                        onClick={handlePieClick}
                        cursor="pointer"
                      >
                        {pieChartData.map((entry, index) => {
                          const isTagChart =
                            chartType === 'tags' ||
                            chartType === 'tags_base_regions' ||
                            chartType === 'tags_regions'

                          // For tag charts, always use the tag color map
                          const color = isTagChart
                            ? tagColorMap[entry.category] ||
                              REGION_COLORS[index % REGION_COLORS.length]
                            : REGION_COLORS[index % REGION_COLORS.length]

                          return <Cell key={`cell-${index}`} fill={color} />
                        })}
                      </Pie>
                      <Tooltip />
                      <Legend
                        layout="vertical"
                        align="right"
                        verticalAlign="middle"
                        wrapperStyle={{
                          paddingLeft: '20px',
                          color: '#555',
                          maxHeight: '450px',
                          width: '500px',
                          overflowY: 'auto',
                          border: '1px solid #e0e0e0',
                          borderRadius: '4px',
                          padding: '8px',
                          boxShadow: 'inset 0 -10px 10px -10px rgba(0,0,0,0.1)',
                        }}
                        formatter={(value) => (
                          <span style={{ color: '#555' }}>{value}</span>
                        )}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  {/* Logo positioned at bottom left */}
                  <Box
                    component="img"
                    src="/Fundsorter-h-black800px.png"
                    alt="Fundsorter"
                    sx={{
                      position: 'absolute',
                      bottom: 20,
                      left: 20,
                      height: 45,
                      opacity: 0.7,
                    }}
                  />
                </Box>
              )}
            {chartType !== 'count' && pieChartData.length > availableTags.length && (
              <Box sx={{ textAlign: 'center', py: 4, mb: 3 }}>
                <Typography variant="body1" color="textSecondary">
                  Chart hidden - too many segments ({pieChartData.length}). Use
                  filters to narrow results or view the table below.
                </Typography>
              </Box>
            )}
            {chartType === 'count' && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="h4" color="primary">
                  {totalOpportunities}
                </Typography>
                <Typography variant="body1" color="textSecondary">
                  opportunities match your filters
                </Typography>
              </Box>
            )}

            {/* Table Breakdown - only show for tags/regions */}
            {chartType !== 'count' && (
              <TableContainer sx={{ overflowX: 'auto', mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>{tableHeader}</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Count</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>% of Filtered Results</strong>
                      </TableCell>
                      <TableCell align="right">
                        <strong>
                          % of{' '}
                          {chartType === 'regions' ||
                          chartType === 'base_regions'
                            ? 'Location'
                            : 'Tag'}{' '}
                          Distribution
                        </strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(() => {
                      const totalCount = stats.reduce(
                        (sum, stat) => sum + stat.count,
                        0
                      )
                      const isTagChart =
                        chartType === 'tags' ||
                        chartType === 'tags_base_regions' ||
                        chartType === 'tags_regions'

                      return stats.map((stat, index) => {
                        const percentageOfOpps =
                          totalOpportunities > 0
                            ? ((stat.count / totalOpportunities) * 100).toFixed(
                                1
                              )
                            : '0.0'

                        const percentageOfDistribution =
                          totalCount > 0
                            ? ((stat.count / totalCount) * 100).toFixed(1)
                            : '0.0'

                        const color =
                          isTagChart && stat.category in tagColorMap
                            ? tagColorMap[stat.category]
                            : REGION_COLORS[index % REGION_COLORS.length]

                        return (
                          <TableRow key={stat.category}>
                            <TableCell>
                              <Box
                                sx={{ display: 'flex', alignItems: 'center' }}
                              >
                                <Box
                                  sx={{
                                    width: 16,
                                    height: 16,
                                    bgcolor: color,
                                    mr: 1,
                                    borderRadius: 1,
                                  }}
                                />
                                {stat.category}
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <strong>{stat.count}</strong>
                            </TableCell>
                            <TableCell align="right">
                              {percentageOfOpps}%
                            </TableCell>
                            <TableCell align="right">
                              {percentageOfDistribution}%
                            </TableCell>
                          </TableRow>
                        )
                      })
                    })()}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  )
}
